import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { EstimateDetail } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Spinner } from '../../components/ui/Spinner';
import { formatMoney } from '../../lib/format';
import { serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

export interface ApplyTemplateDialogProps {
  estimateId: string;
  revision: number;
  /** `null` closes the dialog; a template id opens it and loads the preview. */
  templateId: string | null;
  onClose: () => void;
  onApplied: (detail: EstimateDetail) => void;
}

/** Money-valued fields print as money; everything else prints as it is stored. */
const MONEY_FIELDS = new Set(['referencePrice']);

/**
 * Applying another template's published version, previewed first (PP AC09).
 *
 * The preview is the whole point: replacing the shared values changes a price the customer may already
 * have been told, so the salesperson sees **old → new** for every field it would move, and which
 * overrides the new version would drop, before anything is written. Applying returns the estimate to
 * `DRAFT` and keeps every fact they entered about *this* car — a template describes a model, not a
 * vehicle.
 */
export const ApplyTemplateDialog = ({ estimateId, revision, templateId, onClose, onApplied }: ApplyTemplateDialogProps) => {
  const { t } = useI18nTranslation();
  const [error, setError] = useState<string | null>(null);

  const previewQuery = useQuery(
    orpc.estimates.applyTemplatePreview.queryOptions({
      input: { estimateId, templateId: templateId ?? '' },
      enabled: templateId !== null,
    }),
  );

  const applyTemplate = useMutation(orpc.estimates.applyTemplate.mutationOptions());

  const preview = previewQuery.data ?? null;

  const onConfirm = async (): Promise<void> => {
    if (templateId === null) {
      return;
    }

    setError(null);

    try {
      onApplied(await applyTemplate.mutateAsync({ estimateId, expectedRevision: revision, templateId }));
    } catch (failure: unknown) {
      setError(serverMessage(failure) ?? t(submitErrorKey(failure)));
    }
  };

  const valueOf = (field: string, value: string | number | null): string => {
    if (value === null) {
      return t('common.notRecorded');
    }

    return MONEY_FIELDS.has(field) && typeof value === 'number' ? formatMoney(value) : String(value);
  };

  return (
    <Dialog
      isOpen={templateId !== null}
      onClose={onClose}
      title={t('estimates.applyTemplateTitle')}
      description={t('estimates.applyTemplateBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={applyTemplate.isPending}
            disabled={preview === null}
            onClick={() => void onConfirm()}
          >
            {t('estimates.applyTemplateConfirm')}
          </Button>
        </>
      }
    >
      {previewQuery.isLoading ? (
        <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="size-3" />
          {t('app.loading')}
        </p>
      ) : preview === null ? (
        <p className="text-sm text-destructive">{t('errors.loadFailed')}</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t('estimates.applyTemplateTarget', { name: preview.templateName, version: preview.version })}</p>

          {preview.changes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('estimates.applyTemplateNoChanges')}</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {preview.changes.map((change) => (
                <li
                  key={change.field}
                  className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm"
                >
                  <span className="text-muted-foreground">{t(`estimates.fields.${change.field}`, { defaultValue: change.field })}</span>
                  <span
                    dir="ltr"
                    className="tabular-nums text-foreground"
                  >
                    {valueOf(change.field, change.from)} → {valueOf(change.field, change.to)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {preview.clearedOverrides.length > 0 ? (
            <p className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-sm text-[#a16207]">
              {t('estimates.applyTemplateClearsOverrides', { keys: preview.clearedOverrides.join(', ') })}
            </p>
          ) : null}

          <p className="text-sm text-muted-foreground">{t('estimates.applyTemplateKeepsFacts')}</p>

          {error ? (
            <p
              role="alert"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
};
